/**
 * Post-show results math for a party.
 * Pure helpers only: no database, no UI copy. Every number comes from the
 * party's real ballots and announced winners.
 */

import type { PartyBranding } from "./party-branding";
import { getAnnouncedCategoriesInOrder } from "./tie-breakers";

export type PartyResultsNominee = {
  id: string;
  name: string;
  imageUrl: string | null;
};

export type PartyResultsCategory = {
  id: string;
  name: string;
  orderIndex: number;
  nominees: PartyResultsNominee[];
  winnerNomineeIds: string[];
  /** Earliest announcement time for the category, ISO. */
  announcedAt: string | null;
};

export type PartyResultsMember = {
  userId: string;
  /** Null when the member has no name that is safe to show. The UI supplies a fallback. */
  displayName: string | null;
  imageUrl: string | null;
};

export type PartyVotesByUser = Record<string, Record<string, string>>;

/** Leaderboard order after tie-breakers, as returned by the scoring DAL. */
export type PartyRankedMember = { userId: string; rank: number };

export type PartyResultsSource = {
  categories: PartyResultsCategory[];
  members: PartyResultsMember[];
  votesByUserId: PartyVotesByUser;
  ranked: PartyRankedMember[];
};

export type PartyMemberRef = { userId: string; displayName: string | null };

export type PartyStandingRow = PartyResultsMember & {
  rank: number;
  points: number;
  picksMade: number;
};

export type PartyResultsBestCall = {
  categoryId: string;
  categoryName: string;
  winnerNames: string[];
  callers: PartyMemberRef[];
  voterCount: number;
};

export type PartyResultsUpset = {
  categoryId: string;
  categoryName: string;
  winnerNames: string[];
  favoriteNames: string[];
  favoriteVoteCount: number;
  voterCount: number;
  callers: PartyMemberRef[];
};

export type PartyResultsStats = {
  memberCount: number;
  /** Members with at least one pick. */
  ballotsStarted: number;
  /** Members with a pick in every category. */
  ballotsComplete: number;
  predictionCount: number;
  correctPredictionCount: number;
  /** Share of picks in announced categories that were right, 0 to 1. Null with no picks. */
  groupAccuracy: number | null;
  averagePoints: number;
  /** Announced categories where the room's clear favorite lost. */
  upsetCount: number;
};

export type PartyResultsRecap = {
  complete: boolean;
  totalCategories: number;
  announcedCount: number;
  standings: PartyStandingRow[];
  podium: PartyStandingRow[];
  bestCalls: PartyResultsBestCall[];
  upsets: PartyResultsUpset[];
  stats: PartyResultsStats;
};

type PartyResultsViewBase = {
  partyId: string;
  partyName: string;
  awardShowName: string;
  awardShowYear: number;
  branding: PartyBranding | null;
};

/** Everything the post-show results recap renders. */
export type PartyResultsView = PartyResultsViewBase & { recap: PartyResultsRecap };

export const PARTY_RESULTS_PODIUM_RANK = 3;
export const PARTY_RESULTS_CALLOUT_LIMIT = 3;
/** A best call is a correct pick that at most this share of voters made. */
const BEST_CALL_MAX_SHARE = 0.5;
const UPSET_MIN_FAVORITE_VOTES = 2;

function trimmed(value: string | null | undefined): string | null {
  const result = value?.trim();
  return result ? result : null;
}

/** Username, then name, then the part of the email before the @. Never a full address. */
export function publicMemberDisplayName(identity: {
  username?: string | null;
  name?: string | null;
  email?: string | null;
}): string | null {
  const username = trimmed(identity.username);
  if (username) return username;
  const name = trimmed(identity.name);
  if (name) return name;
  const email = trimmed(identity.email);
  if (!email) return null;
  return trimmed(email.split("@")[0]);
}

/**
 * Matches the vote-visibility rule in the votes DAL: other members' picks stay
 * hidden until the show's start time, and a show with no start time never counts as started.
 */
export function hasPartyShowStarted(
  startTime: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!startTime) return false;
  const start = new Date(startTime).getTime();
  if (Number.isNaN(start)) return false;
  return now.getTime() >= start;
}

function isAnnounced(category: PartyResultsCategory): boolean {
  return category.winnerNomineeIds.length > 0;
}

export function countCorrectPicks(
  categories: readonly PartyResultsCategory[],
  picks: Record<string, string> | undefined,
): number {
  if (!picks) return 0;
  let points = 0;
  for (const category of categories) {
    const pick = picks[category.id];
    if (pick && category.winnerNomineeIds.includes(pick)) {
      points += 1;
    }
  }
  return points;
}

function announcedInOrder(
  categories: readonly PartyResultsCategory[],
): PartyResultsCategory[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  return getAnnouncedCategoriesInOrder(
    categories.map((category) => ({
      id: category.id,
      orderIndex: category.orderIndex,
      resultNomineeIds: category.winnerNomineeIds,
      announcedAt: category.announcedAt,
    })),
  ).flatMap((entry) => {
    const category = byId.get(entry.id);
    return category ? [category] : [];
  });
}

function buildStandings(source: PartyResultsSource): PartyStandingRow[] {
  const memberById = new Map(source.members.map((member) => [member.userId, member]));
  const categoryIds = new Set(source.categories.map((category) => category.id));
  return source.ranked.flatMap((entry) => {
    const member = memberById.get(entry.userId);
    if (!member) return [];
    const picks = source.votesByUserId[member.userId] ?? {};
    return [
      {
        ...member,
        rank: entry.rank,
        points: countCorrectPicks(source.categories, picks),
        picksMade: Object.keys(picks).filter((id) => categoryIds.has(id)).length,
      },
    ];
  });
}

function membersWhoPicked(
  category: PartyResultsCategory,
  nomineeIds: readonly string[],
  standings: readonly PartyStandingRow[],
  votesByUserId: PartyVotesByUser,
): PartyMemberRef[] {
  return standings
    .filter((row) => {
      const pick = votesByUserId[row.userId]?.[category.id];
      return pick !== undefined && nomineeIds.includes(pick);
    })
    .map((row) => ({ userId: row.userId, displayName: row.displayName }));
}

function voteCounts(
  category: PartyResultsCategory,
  memberIds: readonly string[],
  votesByUserId: PartyVotesByUser,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const userId of memberIds) {
    const pick = votesByUserId[userId]?.[category.id];
    if (pick) counts.set(pick, (counts.get(pick) ?? 0) + 1);
  }
  return counts;
}

function nomineeNames(category: PartyResultsCategory, ids: readonly string[]): string[] {
  return ids.flatMap((id) => {
    const nominee = category.nominees.find((entry) => entry.id === id);
    return nominee ? [nominee.name] : [];
  });
}

type CategoryTally = {
  category: PartyResultsCategory;
  voterCount: number;
  callers: PartyMemberRef[];
  favoriteIds: string[];
  favoriteVoteCount: number;
};

function tallyCategory(
  category: PartyResultsCategory,
  standings: readonly PartyStandingRow[],
  votesByUserId: PartyVotesByUser,
): CategoryTally {
  const counts = voteCounts(
    category,
    standings.map((row) => row.userId),
    votesByUserId,
  );
  const favoriteVoteCount = Math.max(0, ...counts.values());
  return {
    category,
    voterCount: [...counts.values()].reduce((sum, count) => sum + count, 0),
    callers: membersWhoPicked(category, category.winnerNomineeIds, standings, votesByUserId),
    favoriteIds: [...counts.entries()]
      .filter(([, count]) => count === favoriteVoteCount && count > 0)
      .map(([id]) => id),
    favoriteVoteCount,
  };
}

function isUpset(tally: CategoryTally): boolean {
  return (
    tally.favoriteIds.length === 1 &&
    tally.favoriteVoteCount >= UPSET_MIN_FAVORITE_VOTES &&
    !tally.category.winnerNomineeIds.includes(tally.favoriteIds[0] ?? "")
  );
}

export function buildPartyResultsRecap(
  source: PartyResultsSource,
  options: { limit?: number } = {},
): PartyResultsRecap {
  const limit = options.limit ?? PARTY_RESULTS_CALLOUT_LIMIT;
  const standings = buildStandings(source);
  const announced = announcedInOrder(source.categories);
  const tallies = announced.map((category) =>
    tallyCategory(category, standings, source.votesByUserId),
  );

  const bestCalls = tallies
    .filter(
      (tally) =>
        tally.callers.length > 0 &&
        tally.callers.length / tally.voterCount <= BEST_CALL_MAX_SHARE,
    )
    .sort(
      (left, right) =>
        left.callers.length / left.voterCount - right.callers.length / right.voterCount ||
        left.callers.length - right.callers.length ||
        left.category.orderIndex - right.category.orderIndex,
    )
    .slice(0, limit)
    .map((tally) => ({
      categoryId: tally.category.id,
      categoryName: tally.category.name,
      winnerNames: nomineeNames(tally.category, tally.category.winnerNomineeIds),
      callers: tally.callers,
      voterCount: tally.voterCount,
    }));

  const allUpsets = tallies.filter(isUpset);
  const upsets = [...allUpsets]
    .sort(
      (left, right) =>
        right.favoriteVoteCount / right.voterCount - left.favoriteVoteCount / left.voterCount ||
        right.voterCount - left.voterCount ||
        left.category.orderIndex - right.category.orderIndex,
    )
    .slice(0, limit)
    .map((tally) => ({
      categoryId: tally.category.id,
      categoryName: tally.category.name,
      winnerNames: nomineeNames(tally.category, tally.category.winnerNomineeIds),
      favoriteNames: nomineeNames(tally.category, tally.favoriteIds),
      favoriteVoteCount: tally.favoriteVoteCount,
      voterCount: tally.voterCount,
      callers: tally.callers,
    }));

  const totalCategories = source.categories.length;
  const announcedIds = new Set(announced.map((category) => category.id));
  let predictionCount = 0;
  let announcedPickCount = 0;
  let ballotsStarted = 0;
  let ballotsComplete = 0;
  for (const row of standings) {
    if (row.picksMade > 0) ballotsStarted += 1;
    if (totalCategories > 0 && row.picksMade >= totalCategories) ballotsComplete += 1;
    predictionCount += row.picksMade;
    announcedPickCount += Object.keys(source.votesByUserId[row.userId] ?? {}).filter((id) =>
      announcedIds.has(id),
    ).length;
  }
  const correctPredictionCount = standings.reduce((sum, row) => sum + row.points, 0);

  return {
    complete: totalCategories > 0 && announced.length >= totalCategories,
    totalCategories,
    announcedCount: announced.length,
    standings,
    podium: standings.filter((row) => row.rank <= PARTY_RESULTS_PODIUM_RANK),
    bestCalls,
    upsets,
    stats: {
      memberCount: standings.length,
      ballotsStarted,
      ballotsComplete,
      predictionCount,
      correctPredictionCount,
      groupAccuracy:
        announcedPickCount > 0 ? correctPredictionCount / announcedPickCount : null,
      averagePoints: standings.length > 0 ? correctPredictionCount / standings.length : 0,
      upsetCount: allUpsets.length,
    },
  };
}
